/**
 * Per-device conveniences kept in localStorage: unsent drafts, collapsed
 * categories, the last channel per space. Never used for shared data.
 */
function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown): void {
  try {
    if (value === undefined || value === null || value === '') localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable or full */
  }
}

export const drafts = {
  get: (channelId: string) => read<string>(`spawnwire:draft:${channelId}`, ''),
  set: (channelId: string, text: string) => write(`spawnwire:draft:${channelId}`, text || undefined),
};

export const collapsed = {
  get: () => new Set(read<string[]>('spawnwire:collapsed', [])),
  toggle: (id: string) => {
    const set = collapsed.get();
    if (set.has(id)) set.delete(id);
    else set.add(id);
    write('spawnwire:collapsed', [...set]);
    return set;
  },
};

export const lastChannel = {
  get: (spaceId: string) => read<string | null>(`spawnwire:last:${spaceId}`, null),
  set: (spaceId: string, channelId: string) => write(`spawnwire:last:${spaceId}`, channelId),
};

/**
 * The name of the microphone you picked. Device ids differ between browsers and computers, so the name is
 * what finds the same microphone again here when the id saved on your account doesn't match.
 */
export const micLabel = {
  get: () => read<string | null>('spawnwire:mic-label', null),
  set: (label: string | null) => write('spawnwire:mic-label', label),
};

/** A support ticket opened while signed out on this device, with the key from its private link. */
export interface SavedTicket {
  id: string;
  number: number;
  subject: string;
  key: string;
}

export const savedTickets = {
  get: () => read<SavedTicket[]>('spawnwire:support-tickets', []),
  save: (ticket: SavedTicket) => write('spawnwire:support-tickets', [ticket, ...savedTickets.get().filter((t) => t.id !== ticket.id)].slice(0, 20)),
  remove: (id: string) => write('spawnwire:support-tickets', savedTickets.get().filter((t) => t.id !== id)),
};

export const themeCache = {
  get: () => read<string | null>('spawnwire:theme', null),
  set: (theme: string) => write('spawnwire:theme', theme),
};
